/**
 * Centralized logger utility for the sg-chatbot client
 * 
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   
 *   logger.debug('Debug message', { context: 'data' });
 *   logger.info('Info message');
 *   logger.warn('Warning message');
 *   logger.error('Error message', error);
 * 
 * Configuration:
 *   - Set via environment variable: REACT_APP_LOG_LEVEL (debug|info|warn|error|off)
 *   - Or programmatically: logger.setLogLevel('debug')
 *   - Or in browser DevTools: localStorage.setItem('sg-chatbot-log-level', 'debug')
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'off';

interface LogContext {
  [key: string]: any;
}

const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  off: 4
};

class Logger {
  private logLevel: LogLevel = 'warn';
  private isDevelopment: boolean = false;
  private namespace: string = 'sg-chatbot';
  private initialized: boolean = false;
  private cachedStoredLevel: LogLevel | null = null;

  constructor() {
    this.initialize();
  }

  /**
   * Initialize logger based on environment and localStorage
   */
  private initialize(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Check for development environment
    this.isDevelopment = 
      process.env.NODE_ENV === 'development' || 
      process.env.REACT_APP_DEBUG_MODE === 'true';

    // Try localStorage first (allows runtime override)
    const storedLevel = localStorage.getItem('sg-chatbot-log-level');
    if (storedLevel && this.isValidLogLevel(storedLevel)) {
      this.logLevel = storedLevel as LogLevel;
      this.cachedStoredLevel = storedLevel as LogLevel;
      console.log(`[Logger] Using localStorage level: ${storedLevel}`);
      return;
    }

    // Then check environment variable
    const envLevel = process.env.REACT_APP_LOG_LEVEL;
    if (envLevel && this.isValidLogLevel(envLevel)) {
      this.logLevel = envLevel as LogLevel;
      this.cachedStoredLevel = envLevel as LogLevel;
    //   console.log(`[Logger] Using environment level: ${envLevel}`);
      return;
    }

    // Default: debug in development, warn in production
    const defaultLevel = this.isDevelopment ? 'debug' : 'warn';
    this.logLevel = defaultLevel;
    this.cachedStoredLevel = defaultLevel;
    console.log(`[Logger] Using default level: ${defaultLevel}`);
  }

  /**
   * Check if provided level is a valid log level
   */
  private isValidLogLevel(level: string): level is LogLevel {
    return Object.keys(LOG_LEVELS).includes(level);
  }

  /**
   * Check if a message at given level should be logged
   * Uses cached localStorage value for performance
   * 
   * Logic: Show message if current level value <= message level value
   * When set to debug(0):
   * debug(0) <= debug(0) → show
   * debug(0) <= info(1) → show
   * debug(0) <= warn(2) → show
   * debug(0) <= error(3) → show
   * 
   * When set to warn(2):
   * warn(2) <= debug(0) → hide
   * warn(2) <= info(1) → hide
   * warn(2) <= warn(2) → show
   * warn(2) <= error(3) → show
   */
  private shouldLog(level: LogLevel): boolean {
    // Use cached localStorage value if available, otherwise use configured level
    const currentLevel = this.cachedStoredLevel ?? this.logLevel;

    if (currentLevel === 'off') {
      return false;
    }
    return LOG_LEVELS[currentLevel] <= LOG_LEVELS[level];
  }

  /**
   * Format log message with timestamp and context
   */
  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    let formatted = `[${timestamp}] [${level.toUpperCase()}]`;
    formatted += ` (${this.namespace})`;
    formatted += ` ${message}`;
    return formatted;
  }

  /**
   * Get console method for log level
   */
  private getConsoleMethod(level: LogLevel): 'log' | 'debug' | 'info' | 'warn' | 'error' {
    switch (level) {
      case 'debug':
        // Use 'log' instead of 'debug' to avoid browser console filtering
        return 'log';
      case 'info':
        return 'info';
      case 'warn':
        return 'warn';
      case 'error':
        return 'error';
      default:
        return 'log';
    }
  }

  /**
   * Log debug message (lowest priority)
   */
  debug(message: string, context?: LogContext): void {
    if (!this.shouldLog('debug')) return;
    
    const formatted = this.formatMessage('debug', message, context);
    const method = this.getConsoleMethod('debug');
    
    if (context) {
      console[method](formatted, context);
    } else {
      console[method](formatted);
    }
  }

  /**
   * Log info message
   */
  info(message: string, context?: LogContext): void {
    if (!this.shouldLog('info')) return;
    
    const formatted = this.formatMessage('info', message, context);
    const method = this.getConsoleMethod('info');
    
    if (context) {
      console[method](formatted, context);
    } else {
      console[method](formatted);
    }
  }

  /**
   * Log warning message
   */
  warn(message: string, context?: LogContext): void {
    if (!this.shouldLog('warn')) return;
    
    const formatted = this.formatMessage('warn', message, context);
    const method = this.getConsoleMethod('warn');
    
    if (context) {
      console[method](formatted, context);
    } else {
      console[method](formatted);
    }
  }

  /**
   * Log error message (highest priority)
   */
  error(message: string, error?: Error | LogContext | unknown): void {
    if (!this.shouldLog('error')) return;
    
    const formatted = this.formatMessage('error', message);
    const method = this.getConsoleMethod('error');
    
    if (error instanceof Error) {
      console[method](formatted, error.message);
      if (error.stack) {
        console[method](error.stack);
      }
    } else if (error && typeof error === 'object') {
      console[method](formatted, error);
    } else if (error) {
      console[method](formatted, error);
    } else {
      console[method](formatted);
    }
  }

  /**
   * Set log level programmatically
   */
  setLogLevel(level: LogLevel | string): void {
    if (this.isValidLogLevel(level)) {
      this.logLevel = level as LogLevel;
      this.cachedStoredLevel = level as LogLevel;
      // Persist to localStorage for next session
      localStorage.setItem('sg-chatbot-log-level', level);
      this.info(`Log level changed to: ${level}`);
    } else {
      console.warn(`Invalid log level: ${level}. Valid options: ${Object.keys(LOG_LEVELS).join(', ')}`);
    }
  }

  /**
   * Get current log level
   */
  getLogLevel(): LogLevel {
    return this.logLevel;
  }

  /**
   * Get isDevelopment flag
   */
  isDev(): boolean {
    return this.isDevelopment;
  }

  /**
   * Reset to default log level
   */
  reset(): void {
    localStorage.removeItem('sg-chatbot-log-level');
    this.cachedStoredLevel = null;
    // Force re-initialization on next log call
    this.initialized = false;
    this.initialize();
    this.info('Logger reset to default settings');
  }

  /**
   * Create a named child logger for a specific module
   */
  getModule(moduleName: string): ModuleLogger {
    return new ModuleLogger(moduleName, this);
  }
}

/**
 * Module-scoped logger with consistent formatting
 */
export class ModuleLogger {
  constructor(private moduleName: string, private parentLogger: Logger) {}

  debug(message: string, context?: LogContext): void {
    this.parentLogger.debug(`[${this.moduleName}] ${message}`, context);
  }

  info(message: string, context?: LogContext): void {
    this.parentLogger.info(`[${this.moduleName}] ${message}`, context);
  }

  warn(message: string, context?: LogContext): void {
    this.parentLogger.warn(`[${this.moduleName}] ${message}`, context);
  }

  error(message: string, error?: Error | LogContext | unknown): void {
    this.parentLogger.error(`[${this.moduleName}] ${message}`, error);
  }
}

// Export singleton instance
export const logger = new Logger();

/**
 * DevTools utilities - exposed for browser console access during development
 */
export const loggerDevTools = {
  /**
   * Enable debug logging in the browser console
   * Usage: In browser console: loggerDevTools.enableDebug()
   */
  enableDebug: () => {
    logger.setLogLevel('debug');
    console.log('✅ Debug logging enabled');
  },

  /**
   * Enable verbose logging (includes everything)
   * Usage: In browser console: loggerDevTools.enableVerbose()
   */
  enableVerbose: () => {
    logger.setLogLevel('debug');
    console.log('✅ Verbose logging enabled (debug level)');
  },

  /**
   * Disable logging completely
   * Usage: In browser console: loggerDevTools.disable()
   */
  disable: () => {
    logger.setLogLevel('off');
    console.log('✅ Logging disabled');
  },

  /**
   * Set log level to a specific level
   * Usage: In browser console: loggerDevTools.setLevel('info')
   */
  setLevel: (level: string) => {
    logger.setLogLevel(level);
  },

  /**
   * Get current log level
   * Usage: In browser console: loggerDevTools.getLevel()
   */
  getLevel: () => {
    console.log(`Current log level: ${logger.getLogLevel()}`);
    return logger.getLogLevel();
  },

  /**
   * Reset to default settings
   * Usage: In browser console: loggerDevTools.reset()
   */
  reset: () => {
    logger.reset();
    console.log('✅ Logger reset to defaults');
  },

  /**
   * Print available commands
   * Usage: In browser console: loggerDevTools.help()
   */
  help: () => {
    console.log(`
Logger Dev Tools - Browser Console Commands:
----------------------------------------------
  loggerDevTools.enableDebug()   - Enable debug logging
  loggerDevTools.enableVerbose() - Enable verbose logging
  loggerDevTools.disable()       - Disable all logging
  loggerDevTools.setLevel(name)  - Set to: debug|info|warn|error|off
  loggerDevTools.getLevel()      - Show current level
  loggerDevTools.reset()         - Reset to defaults
  loggerDevTools.help()          - Show this help
    `);
  }
};

// Expose to window in development for easy console access
if (typeof window !== 'undefined') {
  (window as any).loggerDevTools = loggerDevTools;
}
